// frontend/packages/business-console/src/services/quoteService.js
//
// Thin wrapper over /api/v2/crm/quotes (work item 2.11). Business
// context is supplied via the x-business-id header (axios interceptor
// in @upryzze/services); callers don't pass businessId.

import { api } from '@upryzze/services';

/**
 * GET /api/v2/crm/quotes
 * @param {object} params
 *   state?      — filter (draft|sent|viewed|accepted|declined|expired|...)
 *   customerId? — filter by customer
 *   q?          — search (matches Q-#### prefix + customer first/last name)
 *   before?     — cursor (ISO date; pagination)
 *   limit?      — page size (default 25, max 100)
 */
export async function listQuotes(params = {}) {
  const { data } = await api.get('/v2/crm/quotes', { params });
  return data || { items: [], hasMore: false, nextCursor: null };
}

export async function getQuote(id) {
  const { data } = await api.get(`/v2/crm/quotes/${encodeURIComponent(id)}`);
  return data;
}

export async function createQuoteDraft(payload, { idempotencyKey } = {}) {
  const config = idempotencyKey
    ? { headers: { 'Idempotency-Key': idempotencyKey } }
    : undefined;
  const { data } = await api.post('/v2/crm/quotes', payload, config);
  return data;
}

export async function updateQuoteDraft(id, payload, { idempotencyKey } = {}) {
  const config = idempotencyKey
    ? { headers: { 'Idempotency-Key': idempotencyKey } }
    : undefined;
  const { data } = await api.patch(
    `/v2/crm/quotes/${encodeURIComponent(id)}`,
    payload,
    config
  );
  return data;
}

export async function sendQuote(id) {
  const { data } = await api.post(
    `/v2/crm/quotes/${encodeURIComponent(id)}/send`
  );
  return data;
}

/**
 * POST /api/v2/crm/quotes/:id/resend — re-deliver a sent/viewed estimate's
 * email + PDF link WITHOUT changing state. Distinct from sendQuote, which
 * transitions draft → sent.
 * @param {string} id
 */
export async function resendQuote(id) {
  const { data } = await api.post(
    `/v2/crm/quotes/${encodeURIComponent(id)}/resend`
  );
  return data;
}

/**
 * POST /api/v2/crm/quotes/:id/accept — operator "mark as accepted".
 * Records an out-of-band acceptance (phone / in person) so the estimate
 * can be converted to an invoice. Both fields are required server-side.
 * @param {string} id
 * @param {{ name: string, note: string }} payload
 */
export async function acceptQuote(id, { name, note }) {
  const { data } = await api.post(
    `/v2/crm/quotes/${encodeURIComponent(id)}/accept`,
    { name, note }
  );
  return data;
}

export async function declineQuote(id, reason) {
  const { data } = await api.post(
    `/v2/crm/quotes/${encodeURIComponent(id)}/decline`,
    reason ? { reason } : {}
  );
  return data;
}

export async function convertQuoteToInvoice(id) {
  const { data } = await api.post(
    `/v2/crm/quotes/${encodeURIComponent(id)}/convert`
  );
  return data;
}

export async function deleteQuoteDraft(id) {
  const { data } = await api.delete(`/v2/crm/quotes/${encodeURIComponent(id)}`);
  return data;
}
